"use client";

import { Slot } from "radix-ui";
import type { ComponentPropsWithRef } from "react";
import { useId, useMemo } from "react";
import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";
import { Label } from "@/common/components-2/label";
import { cn } from "@/services/styles/cn";

import { FormFieldContext } from "./form-field-context";
import { FormItemContext } from "./form-item-context";
import { useFormField } from "./use-form-field";

export { FormProvider as Form } from "react-hook-form";

export function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: ControllerProps<TFieldValues, TName>) {
  return (
    <FormFieldContext value={useMemo(() => ({ name: props.name }), [props.name])}>
      <Controller {...props} />
    </FormFieldContext>
  );
}

export interface FormItemProps extends ComponentPropsWithRef<"div"> {}

export function FormItem({ className, ...props }: FormItemProps) {
  const id = useId();

  return (
    <FormItemContext value={useMemo(() => ({ id }), [id])}>
      <div data-slot="form-item" className={cn("flex flex-col gap-2", className)} {...props} />
    </FormItemContext>
  );
}

export interface FormLabelProps extends ComponentPropsWithRef<typeof Label> {}

export function FormLabel({ className, ...props }: FormLabelProps) {
  const { error, formItemId } = useFormField();

  return (
    <Label
      data-slot="form-label"
      className={cn(error && "text-destructive", className)}
      htmlFor={formItemId}
      {...props}
    />
  );
}

export interface FormControlProps extends ComponentPropsWithRef<typeof Slot.Root> {}

export function FormControl(props: FormControlProps) {
  const { error, formItemId, formDescriptionId, formMessageId, isTouched, isDirty } = useFormField();

  return (
    <Slot.Root
      data-slot="form-control"
      id={formItemId}
      aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId}
      aria-invalid={error ? true : isTouched && isDirty ? false : undefined}
      {...props}
    />
  );
}

export interface FormDescriptionProps extends ComponentPropsWithRef<"p"> {}

export function FormDescription({ className, ...props }: FormDescriptionProps) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export interface FormMessageProps extends ComponentPropsWithRef<"p"> {}

export function FormMessage({ className, children, ...props }: FormMessageProps) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error.message) : children;

  if (!body) {
    return null;
  }

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn("font-medium text-destructive text-sm", className)}
      {...props}
    >
      {body}
    </p>
  );
}
